import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { dashboardService } from '../services/dataService';
import { useAuth } from '../hooks/useAuth';
import { isRestrictedVendor } from '../utils/permissions';
import { downloadCsv } from '../utils/csv';
import { percent } from '../components/DetailDashboard';
import BarChart from '../components/charts/BarChart';
import LineChart from '../components/charts/LineChart';
import DateRangeFilter, { presetRange } from '../components/DateRangeFilter';
import Pagination from '../components/Pagination';
import SearchableSelect from '../components/SearchableSelect';
import styles from './assets/css/DashboardPage.module.css';

const PAGE_SIZE = 10;

const LIFECYCLE_STATES = {
  active: 'Active',
  inactive: 'Inactive',
  maintenance: 'Maintenance',
  disabled: 'Disabled',
  retired: 'Retired',
};

const OPERATIONAL_STATUSES = {
  operational: 'Operational',
  non_operational: 'Non-operational',
  maintenance: 'Maintenance',
  unknown: 'Unknown',
};

const LINKAGE = { all: 'All equipment', linked: 'Linked', not_linked: 'Not linked' };

const FUNNEL_SERIES = [
  { key: 'requests', label: 'All requests', color: 'var(--series-1)' },
  { key: 'pulled', label: 'Pulled', color: 'var(--series-2)' },
  { key: 'completed', label: 'Completed', color: 'var(--series-3)' },
  { key: 'verified', label: 'SHA verified', color: 'var(--series-4)' },
];

const CLAIM_SERIES = [
  { key: 'requests', label: 'All requests', color: 'var(--series-1)' },
  { key: 'with_preauth', label: 'With preauth', color: 'var(--series-2)' },
  { key: 'with_claim', label: 'With claim ID', color: 'var(--series-3)' },
];

const BREAKDOWNS = [
  { key: 'by_vendor', label: 'Vendors', column: 'Vendor' },
  { key: 'by_facility', label: 'Facilities', column: 'Facility' },
  { key: 'by_procedure', label: 'SHA procedures', column: 'Procedure' },
  { key: 'by_county', label: 'Counties', column: 'County' },
];

const currency = new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', maximumFractionDigits: 0 });
const formatAmount = (value) => currency.format(Number(value || 0));

const formatPeriod = (granularity) => (period, long) => {
  const date = new Date(`${period}T00:00:00`);
  if (granularity === 'month') {
    return date.toLocaleDateString(undefined, { month: long ? 'long' : 'short', year: 'numeric' });
  }
  return date.toLocaleDateString(undefined, long
    ? { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }
    : { day: 'numeric', month: 'short' });
};

// Counties are compared ignoring case and spacing, as the API filters them.
const sameCounty = (a, b) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

const defaultFilters = () => {
  const { start, end } = presetRange('this_month');
  return {
    start_date: start,
    end_date: end,
    modality: '',
    vendor_id: '',
    county: '',
    facility_id: '',
    lifecycle_state: '',
    operational_status: '',
  };
};

const Kpi = ({ label, value, hint, accent }) => (
  <div className={`${styles.kpi} ${accent ? styles.kpiAccent : ''}`}>
    <span className={styles.kpiLabel}>{label}</span>
    <strong className={styles.kpiValue}>{value}</strong>
    {hint ? <span className={styles.kpiHint}>{hint}</span> : null}
  </div>
);

const Panel = ({ title, note, children, wide }) => (
  <section className={`${styles.panel} ${wide ? styles.panelWide : ''}`}>
    <header className={styles.panelHeader}>
      <h2>{title}</h2>
      {note ? <span className={styles.panelNote}>{note}</span> : null}
    </header>
    {children}
  </section>
);

const BreakdownTable = ({ rows, column, loading }) => {
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [rows]);
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  return (
    <>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{column}</th>
              <th className={styles.number}>Requests</th>
              <th className={styles.number}>Pulled</th>
              <th className={styles.number}>Completed</th>
              <th className={styles.number}>Total amount</th>
              <th className={styles.number}>Amount verified</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => (
              <tr key={row.key}>
                <td>
                  {row.name}
                  {row.name !== row.key && column === 'Procedure' ? <span className={styles.subText}>{row.key}</span> : null}
                </td>
                <td className={styles.number}>{row.requests.toLocaleString()}</td>
                <td className={styles.number}>
                  {row.pulled.toLocaleString()}
                  <span className={styles.subText}>{percent(row.pulled, row.requests)}%</span>
                </td>
                <td className={styles.number}>
                  {row.completed.toLocaleString()}
                  <span className={styles.subText}>{percent(row.completed, row.requests)}%</span>
                </td>
                <td className={styles.number}>{formatAmount(row.amount)}</td>
                <td className={styles.number}>
                  {formatAmount(row.verified_amount)}
                  <span className={styles.subText}>{row.verified.toLocaleString()} verified</span>
                </td>
              </tr>
            ))}
            {!pageRows.length ? (
              <tr>
                <td colSpan={6} className={styles.muted}>{loading ? 'Loading…' : 'No requests in this range.'}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pageSize={PAGE_SIZE} total={rows.length} loading={loading} onPageChange={setPage} />
    </>
  );
};

const DashboardPage = () => {
  const { user } = useAuth();
  const isVendorUser = isRestrictedVendor(user);
  const [filters, setFilters] = useState(defaultFilters);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [breakdown, setBreakdown] = useState(BREAKDOWNS[0].key);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value));
      setData(await dashboardService.get(params));
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value }));

  const summary = data?.summary;
  const options = data?.options || { vendors: [], facilities: [], modalities: [], counties: [] };
  const facilityOptions = filters.county
    ? options.facilities.filter((facility) => sameCounty(facility.county, filters.county))
    : options.facilities;
  // A facility outside the chosen county would leave nothing to show, so it is cleared.
  const setCounty = (county) => setFilters((current) => {
    const facility = options.facilities.find((item) => item.id === current.facility_id);
    const keepFacility = !county || !facility || sameCounty(facility.county, county);
    return { ...current, county, facility_id: keepFacility ? current.facility_id : '' };
  });
  const activeBreakdown = BREAKDOWNS.find((item) => item.key === breakdown);
  const breakdownRows = useMemo(() => data?.[breakdown] || [], [data, breakdown]);
  const periodLabel = useMemo(() => formatPeriod(data?.granularity), [data?.granularity]);

  const asBars = (counts, labels = {}) => (counts || []).map((item) => ({
    key: item.key,
    label: labels[item.key] || item.key,
    value: item.count,
  }));

  const exportBreakdown = () => downloadCsv(
    `dashboard-${breakdown.replace('by_', '')}_${filters.start_date || 'all'}_${filters.end_date || 'all'}.csv`,
    breakdownRows,
    [
      ...(breakdown === 'by_procedure' ? [['key', 'Code']] : []),
      ['name', activeBreakdown.column],
      ['requests', 'Requests'],
      ['pulled', 'Pulled'],
      ['completed', 'Completed'],
      ['verified', 'Verified'],
      ['amount', 'Total amount'],
      ['verified_amount', 'Amount verified'],
    ],
  );

  const show = (value) => (summary ? value : '--');

  return (
    <div className={styles.dashboard}>
      <section className={styles.filters}>
        <DateRangeFilter
          start={filters.start_date}
          end={filters.end_date}
          onChange={({ start, end }) => setFilters((current) => ({ ...current, start_date: start, end_date: end }))}
        />
        <div className={styles.filterFields}>
          <label className={styles.field}>
            <span>Modality</span>
            <select value={filters.modality} onChange={(e) => setFilter('modality', e.target.value)}>
              <option value="">All modalities</option>
              {options.modalities.map((modality) => <option key={modality} value={modality}>{modality}</option>)}
            </select>
          </label>
          {!isVendorUser ? (
            <label className={styles.field}>
              <span>Vendor</span>
              <select value={filters.vendor_id} onChange={(e) => setFilter('vendor_id', e.target.value)}>
                <option value="">All vendors</option>
                {options.vendors.map((vendor) => <option key={vendor.id} value={vendor.id}>{vendor.name}</option>)}
              </select>
            </label>
          ) : null}
          <label className={styles.field}>
            <span>County</span>
            <select value={filters.county} onChange={(e) => setCounty(e.target.value)}>
              <option value="">All counties</option>
              {(options.counties || []).map((county) => <option key={county} value={county}>{county}</option>)}
            </select>
          </label>
          <div className={styles.field}>
            <span>Facility</span>
            <SearchableSelect
              className={styles.facilitySelect}
              options={facilityOptions}
              value={filters.facility_id}
              onChange={(value) => setFilter('facility_id', value)}
              emptyLabel="All facilities"
              ariaLabel="Facility"
            />
          </div>
          <label className={styles.field}>
            <span>Equipment status</span>
            <select value={filters.lifecycle_state} onChange={(e) => setFilter('lifecycle_state', e.target.value)}>
              <option value="">Any status</option>
              {Object.entries(LIFECYCLE_STATES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            <span>Operational status</span>
            <select value={filters.operational_status} onChange={(e) => setFilter('operational_status', e.target.value)}>
              <option value="">Any operational status</option>
              {Object.entries(OPERATIONAL_STATUSES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <button type="button" className={styles.resetButton} onClick={() => setFilters(defaultFilters())}>
            Reset
          </button>
        </div>
      </section>

      {error ? <div className={styles.error}>{error}</div> : null}

      <div className={`${styles.content} ${loading && data ? styles.refreshing : ''}`}>
        <div className={styles.kpis}>
          <Kpi label="Total requests" value={show(summary?.total_requests.toLocaleString())} hint={summary ? `${summary.pulled_requests.toLocaleString()} pulled` : null} />
          <Kpi
            label="Completed requests"
            value={show(summary?.completed_requests.toLocaleString())}
            hint={summary ? `${percent(summary.completed_requests, summary.total_requests)}% of requests` : null}
          />
          <Kpi
            label="Verified requests"
            value={show(summary?.verified_requests.toLocaleString())}
            hint={summary ? `${percent(summary.verified_requests, summary.completed_requests)}% of completed` : null}
          />
          <Kpi label="Amount to vendors" value={show(formatAmount(summary?.vendor_amount))} hint={summary ? `of ${formatAmount(summary.total_amount)} completed` : null} accent />
          <Kpi label="Amount to facilities" value={show(formatAmount(summary?.facility_amount))} accent />
          <Kpi
            label="Verified to vendors"
            value={show(formatAmount(summary?.verified_vendor_amount))}
            hint={summary ? `${percent(summary.verified_vendor_amount, summary.vendor_amount)}% of vendor amount` : null}
            accent
          />
        </div>

        <div className={styles.grid}>
          <Panel title="Equipment by operational status" note="As at now">
            <BarChart items={asBars(data?.equipment_by_operational_status, OPERATIONAL_STATUSES)} emptyText="No equipment matches these filters." />
          </Panel>
          <Panel title="Equipment by linkage" note="As at now">
            <BarChart items={asBars(data?.equipment_by_linkage, LINKAGE)} emptyText="No equipment matches these filters." />
          </Panel>
          <Panel title="Facilities by modality" note="Facilities with that equipment">
            <BarChart items={asBars(data?.facilities_by_modality)} emptyText="No equipment matches these filters." />
          </Panel>
          <Panel title="Requests by modality">
            <BarChart items={asBars(data?.requests_by_modality)} emptyText="No requests in this range." />
          </Panel>

          <Panel title="Request progress" note="By request date" wide>
            <LineChart points={data?.trend} series={FUNNEL_SERIES} formatPeriod={periodLabel} emptyText="No requests in this range." />
          </Panel>
          <Panel title="Preauth and claims" note="By request date" wide>
            <LineChart points={data?.trend} series={CLAIM_SERIES} formatPeriod={periodLabel} emptyText="No requests in this range." />
          </Panel>

          <Panel title="Breakdown" wide>
            <div className={styles.tabsRow}>
              <div className={styles.tabs} role="tablist">
                {BREAKDOWNS.map((item) => (
                  <button
                    type="button"
                    role="tab"
                    key={item.key}
                    aria-selected={breakdown === item.key}
                    className={`${styles.tab} ${breakdown === item.key ? styles.tabActive : ''}`}
                    onClick={() => setBreakdown(item.key)}
                  >
                    {item.label}
                    <span className={styles.tabCount}>{(data?.[item.key] || []).length}</span>
                  </button>
                ))}
              </div>
              <button type="button" className={styles.resetButton} onClick={exportBreakdown} disabled={!breakdownRows.length}>
                Export CSV
              </button>
            </div>
            <BreakdownTable rows={breakdownRows} column={activeBreakdown.column} loading={loading} />
          </Panel>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
